// Tanner Fry
// tannerf1101@yahoo.com
// The redux slice for the user.

import { createSlice } from '@reduxjs/toolkit';
import { Color } from 'react-color';
import { SubscriptionPlan } from '../assets/components/data/SubscriptionData';
import { IUser } from '../interfaces/user';

export interface IMainUserSlice {
    mainUserSlice: {
        // User
        user: IUser;

        // Personalization
        darkTheme: boolean;
        hardinessZone: number;
        location: string;
        temperatureUnit: string;
        subscriptionPlan: SubscriptionPlan;

        // Activity
        buttonClicks: number;
        isLoggedIn: boolean;
        lastLocation: string;
        lastLocationUnauthenticated: string;

        // OTP
        resetEmail: string;
        hasVerifiedOTP: boolean;

        // Preferences
        userPreferences: {
            appThemeColor: string,
            menuThemeColor: string,
            idleTimeoutEnabled: boolean,
            maintenanceTooltipEnabled: boolean,
            notificationPopupEnabled: boolean,
            dashboardPrimaryColor: Color;
            dashboardSecondaryColor: Color;
        }
    }
}

export const MainUserSlice = createSlice({
  name: 'mainUserSlice',
  initialState: {
    // User
    user: {
        uuid: "",
        firstName: "",
        lastName: "",
        email: "",
        isActive: false,
        hasOtpSetup: false,
    },

    // Personalization
    darkTheme: true,
    hardinessZone: -1,
    location: "",
    temperatureUnit: "F",
    subscriptionPlan: SubscriptionPlan.Free,

    // Activity
    buttonClicks: 0,  // Used to determine overall activity
    isLoggedIn: false,
    lastLocation: "",
    lastLocationUnauthenticated: "",

    // OTP
    resetEmail: "",
    hasVerifiedOTP: false,

    // Preferences
    userPreferences: {
        appThemeColor: "rgba(245, 222, 179, 1)",
        menuThemeColor: "rgba(27, 30, 35, 1)",
        idleTimeoutEnabled: true,
        maintenanceTooltipEnabled: true,
        notificationPopupEnabled: true,
        dashboardPrimaryColor: "rgba(27, 30, 35, 1)",
        dashboardSecondaryColor: "rgba(245, 222, 179, 1)",
    }
  },
  reducers: {
    // User 
    setUser: (state, action) => {
        state.user = action.payload;
    },

    // Personalization
    setHardinessZone: (state, action) => {
        state.hardinessZone = action.payload;
    },
    setLocation: (state, action) => {
        state.location = action.payload;
    },
    setTemperatureUnit: (state, action) => {
        state.temperatureUnit = action.payload;
    },
    setSubscriptionStatus: (state, action) => {
        state.subscriptionPlan = action.payload;
    },

    // Activity
    incrementUserClick: (state) => {
        state.buttonClicks += 1
    },
    decrementUserClicks: (state) => {
        state.buttonClicks -= 1
    },
    setLoggedIn: (state, action) => {
        state.isLoggedIn = action.payload;
    },
    setLastLocation: (state, action) => {
        state.lastLocation = action.payload;
    },
    setLastLocationUnauthenticated: (state, action) => {
        state.lastLocationUnauthenticated = action.payload;
    },

    // OTP
    setResetEmail: (state, action) => {
        state.resetEmail = action.payload;
    },
    setHasVerifiedOTP: (state, action) => {
        state.hasVerifiedOTP = action.payload;
    },

    // Preferences
    setUserPreferences: (state, action) => {
        state.userPreferences = action.payload;
    },
  },
})

// Action creators are generated for each case reducer function
export const {
    // User
    setUser,

    // Personalization
    setHardinessZone,
    setLocation,
    setTemperatureUnit,
    setSubscriptionStatus,

    // Activity
    incrementUserClick,
    decrementUserClicks,
    setLoggedIn,
    setLastLocation,
    setLastLocationUnauthenticated,

    // OTP
    setResetEmail,
    setHasVerifiedOTP,

    // Preferences
    setUserPreferences,
} = MainUserSlice.actions

export default MainUserSlice.reducer